import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, Plus, Trash2, Search, X, Loader2, Check, AlertCircle, ClipboardList, Sparkles } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useUserStore } from '@/store/userStore';
import { useTelegram } from '@/hooks/useTelegram';
import type { Exercise, WorkoutLevel } from '@/types/workout';

interface CustomExercise {
  exercise_id: string;
  name: string;
  gif_url: string | null;
  sets: string;
  reps: string;
  rest_seconds: string;
}

interface CustomSession {
  localId: string;
  name: string;
  exercises: CustomExercise[];
  scheduledDay: number | null; // 1=Пн ... 7=Вс
}

const SESSION_NAMES = ['День А', 'День Б', 'День В', 'День Г', 'День Д', 'День Е', 'День Ж'];
const DAYS_SHORT = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

// Рекомендуемые дни по кол-ву тренировок
const DEFAULT_DAYS: Record<number, number[]> = {
  1: [1],
  2: [1, 4],
  3: [1, 3, 5],
  4: [1, 2, 4, 5],
  5: [1, 2, 3, 5, 6],
  6: [1, 2, 3, 4, 5, 6],
  7: [1, 2, 3, 4, 5, 6, 7],
};

const LEVEL_OPTIONS: { value: WorkoutLevel; label: string; desc: string; reps: string }[] = [
  { value: 'beginner',     label: 'Новичок',     desc: 'до 6 мес', reps: '12-15 повт, 2-3 подх' },
  { value: 'intermediate', label: 'Бывалый',     desc: '6 мес — 2 года', reps: '8-12 повт, 3-4 подх' },
  { value: 'advanced',     label: 'Продвинутый', desc: '2+ года', reps: '5-8 повт, 4-5 подх' },
];

// Рекомендации восстановления: сколько дней нужно между тренировками
const MIN_REST_DAYS = 1; // минимум 1 день отдыха между тренировками

export default function CustomWorkoutScreen() {
  const nav = useNavigate();
  const { profile } = useUserStore();
  const { haptic } = useTelegram();

  const [planName, setPlanName] = useState('');
  const [level, setLevel] = useState<WorkoutLevel>('intermediate');
  const [sessions, setSessions] = useState<CustomSession[]>([
    { localId: '1', name: 'День А', exercises: [], scheduledDay: 1 },
  ]);
  const [saving, setSaving] = useState(false);

  const [pickerSession, setPickerSession] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Exercise[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Импорт плана текстом
  const [showImport, setShowImport] = useState(false);
  const [importText, setImportText] = useState('');
  const [importing, setImporting] = useState(false);
  const [replaceTarget, setReplaceTarget] = useState<{ sessionId: string; exIdx: number } | null>(null);
  const [autoProgression, setAutoProgression] = useState(false);

  // Проверяем конфликты дней (тренировки в соседние дни)
  const dayConflicts = (() => {
    const days = sessions.map(s => s.scheduledDay).filter(Boolean) as number[];
    const conflicts: number[] = [];
    days.forEach((d, i) => {
      days.forEach((d2, j) => {
        if (i !== j && Math.abs(d - d2) < MIN_REST_DAYS + 1) conflicts.push(d);
      });
    });
    return new Set(conflicts);
  })();

  function addSession() {
    haptic('light');
    const idx = sessions.length;
    const defaultDay = DEFAULT_DAYS[idx + 1]?.[idx] ?? null;
    setSessions(prev => [
      ...prev,
      { localId: Date.now().toString(), name: SESSION_NAMES[idx] ?? `День ${idx + 1}`, exercises: [], scheduledDay: defaultDay },
    ]);
  }

  function removeSession(localId: string) {
    haptic('light');
    setSessions(prev => prev.filter(s => s.localId !== localId));
  }

  function updateSessionName(localId: string, name: string) {
    setSessions(prev => prev.map(s => s.localId === localId ? { ...s, name } : s));
  }

  function setSessionDay(localId: string, day: number) {
    haptic('light');
    setSessions(prev => prev.map(s =>
      s.localId === localId ? { ...s, scheduledDay: s.scheduledDay === day ? null : day } : s
    ));
  }

  function updateExerciseField(sessionId: string, exIdx: number, field: keyof CustomExercise, value: string) {
    setSessions(prev =>
      prev.map(s =>
        s.localId === sessionId
          ? { ...s, exercises: s.exercises.map((ex, i) => i === exIdx ? { ...ex, [field]: value } : ex) }
          : s
      )
    );
  }

  function removeExercise(sessionId: string, exIdx: number) {
    haptic('light');
    setSessions(prev =>
      prev.map(s =>
        s.localId === sessionId
          ? { ...s, exercises: s.exercises.filter((_, i) => i !== exIdx) }
          : s
      )
    );
  }

  function openPicker(sessionId: string, exIdx?: number) {
    haptic('light');
    setPickerSession(sessionId);
    setReplaceTarget(exIdx !== undefined ? { sessionId, exIdx } : null);
    setSearchQuery('');
    setSearchResults([]);
  }

  function closePicker() {
    setPickerSession(null);
    setReplaceTarget(null);
    setSearchQuery('');
    setSearchResults([]);
  }

  function handleSearchChange(q: string) {
    setSearchQuery(q);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (!q.trim()) { setSearchResults([]); return; }
    setSearchLoading(true);
    searchTimer.current = setTimeout(async () => {
      const { data } = await supabase
        .from('exercises')
        .select('id, name, gif_url, target, equipment, muscle_group, category, secondary_muscles, instructions_en, instructions_ru, image_url')
        .ilike('name', `%${q}%`)
        .limit(30);
      setSearchResults((data ?? []) as Exercise[]);
      setSearchLoading(false);
    }, 400);
  }

  function pickExercise(ex: Exercise) {
    if (!pickerSession) return;
    haptic('light');
    const defaults = {
      beginner:     { sets: '3', reps: '12-15', rest: '120' },
      intermediate: { sets: '3', reps: '8-12',  rest: '90'  },
      advanced:     { sets: '4', reps: '5-8',   rest: '150' },
    }[level];
    if (replaceTarget) {
      // Заменяем упражнение в конкретном слоте (для импорта — уточнение непойманного)
      setSessions(prev =>
        prev.map(s =>
          s.localId === replaceTarget.sessionId
            ? { ...s, exercises: s.exercises.map((old, i) =>
                i === replaceTarget.exIdx
                  ? { ...old, exercise_id: ex.id, name: ex.name, gif_url: ex.gif_url }
                  : old) }
            : s
        )
      );
    } else {
      setSessions(prev =>
        prev.map(s =>
          s.localId === pickerSession
            ? { ...s, exercises: [...s.exercises, { exercise_id: ex.id, name: ex.name, gif_url: ex.gif_url, sets: defaults.sets, reps: defaults.reps, rest_seconds: defaults.rest }] }
            : s
        )
      );
    }
    closePicker();
  }

  // Ищем упражнение в библиотеке: сначала по всей фразе, затем по самому длинному слову
  async function findExercise(name: string): Promise<Exercise | null> {
    const cols = 'id, name, gif_url, target, equipment, muscle_group, category, secondary_muscles, instructions_en, instructions_ru, image_url';
    const q = name.trim();
    if (!q) return null;
    let { data } = await supabase.from('exercises').select(cols).ilike('name', `%${q}%`).limit(1);
    if (!data || data.length === 0) {
      const word = q.split(/\s+/).sort((a, b) => b.length - a.length)[0];
      if (word && word.length >= 4) {
        const r = await supabase.from('exercises').select(cols).ilike('name', `%${word}%`).limit(1);
        data = r.data as any;
      }
    }
    return (data && data[0]) ? (data[0] as Exercise) : null;
  }

  async function runImport() {
    const text = importText.trim();
    if (text.length < 5) { alert('Вставь план текстом'); return; }
    setImporting(true);
    haptic('medium');
    try {
      const { data, error } = await supabase.functions.invoke('parse-workout-text', { body: { text } });
      if (error || !data?.sessions?.length) {
        alert('Не удалось разобрать план. Проверь текст и попробуй ещё раз.');
        return;
      }
      const restByLevel = ({ beginner: '120', intermediate: '90', advanced: '150' } as Record<string, string>)[level];
      const newSessions: CustomSession[] = [];
      let unmatched = 0;
      const importedSessions = data.sessions.slice(0, 4); // лимит плана — 4 дня
      const overflow = data.sessions.length - importedSessions.length;
      const total = importedSessions.length;
      for (let si = 0; si < total; si++) {
        const s = importedSessions[si];
        const exercises: CustomExercise[] = [];
        for (const ex of s.exercises ?? []) {
          const match = await findExercise(ex.name);
          if (!match) unmatched++;
          exercises.push({
            exercise_id: match?.id ?? '',
            name: match?.name ?? ex.name,
            gif_url: match?.gif_url ?? null,
            sets: String(ex.sets ?? 3),
            reps: String(ex.reps ?? '10-12'),
            rest_seconds: restByLevel,
          });
        }
        newSessions.push({
          localId: `${Date.now()}_${si}`,
          name: s.name || SESSION_NAMES[si] || `День ${si + 1}`,
          exercises,
          scheduledDay: s.scheduled_day ?? (DEFAULT_DAYS[total]?.[si] ?? null),
        });
      }
      setSessions(newSessions);
      if (!planName.trim()) setPlanName('Мой план');
      setShowImport(false);
      setImportText('');
      haptic('success');
      if (unmatched > 0 || overflow > 0) {
        const parts: string[] = [];
        if (unmatched > 0) parts.push(`${unmatched} упр. не нашлись (⚠) — нажми и выбери из списка`);
        if (overflow > 0) parts.push('взяли первые 4 дня (лимит плана)');
        alert('Готово! ' + parts.join('. '));
      }
    } catch {
      alert('Ошибка импорта. Попробуй ещё раз.');
    } finally {
      setImporting(false);
    }
  }

  async function savePlan() {
    if (!planName.trim()) { alert('Введи название плана'); return; }
    if (sessions.some(s => s.exercises.length === 0)) {
      alert('В каждой тренировке должно быть хотя бы одно упражнение');
      return;
    }
    if (sessions.some(s => s.exercises.some(ex => !ex.exercise_id))) {
      alert('Есть упражнения без совпадения (⚠). Нажми на них и выбери из библиотеки.');
      return;
    }
    if (sessions.length > 4) {
      alert('Максимум 4 тренировочных дня в одном плане. Убери лишние дни.');
      return;
    }
    setSaving(true);
    haptic('medium');

    const planData = {
      sessions: sessions.map((s, i) => ({
        day: i + 1,
        name: s.name,
        scheduled_day: s.scheduledDay,
        exercises: s.exercises.map(ex => ({
          exercise_id: ex.exercise_id,
          sets: parseInt(ex.sets) || 3,
          reps: ex.reps || '10-12',
          rest_seconds: parseInt(ex.rest_seconds) || 90,
        })),
        warmup_notes: '5-7 мин: лёгкое кардио + динамическая растяжка',
        cooldown_notes: '5 мин: статическая растяжка рабочих мышц',
      })),
      methodology_notes: 'Кастомный план',
      disclaimer: 'Следи за техникой выполнения упражнений.',
      auto_progression: autoProgression,
    };

    const { error } = await supabase.from('workout_plans').insert({
      user_id: profile?.id,
      name: planName.trim(),
      goal: (profile as any)?.goal ?? 'maintain',
      level,
      days_per_week: sessions.length,
      emphasis: 'balanced',
      equipment: [],
      weekly_sets_per_group: 0,
      current_week: 1,
      plan_data: planData,
    });

    setSaving(false);
    if (error) {
      console.error('[savePlan] insert error:', error);
      alert('Ошибка сохранения: ' + (error.message || 'попробуй ещё раз'));
      return;
    }
    haptic('success');
    nav('/chat?tab=workout');
  }

  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex items-center gap-3 px-4 pt-4 pb-3 border-b border-line">
        <button onClick={() => nav('/chat?tab=workout')} className="p-2 -ml-2">
          <ChevronLeft size={20} />
        </button>
        <div className="flex-1">
          <div className="font-semibold text-sm">Свой план тренировок</div>
          <div className="text-[10px] text-ink-dim">Составь программу вручную</div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-5 pb-32">

        {/* Импорт плана текстом */}
        <button
          onClick={() => { haptic('light'); setShowImport(true); }}
          className="w-full flex items-center gap-3 bg-emerald-glow/10 border border-emerald-glow/30 rounded-2xl px-4 py-3 active:scale-[0.99] transition-all"
        >
          <div className="w-9 h-9 rounded-xl bg-emerald-glow/15 flex items-center justify-center shrink-0">
            <ClipboardList size={18} className="text-emerald-glow" />
          </div>
          <div className="flex-1 text-left">
            <div className="text-[13px] font-semibold text-emerald-glow">Вставить план текстом</div>
            <div className="text-[10px] text-ink-dim">Свой план или от тренера — распознаем сами</div>
          </div>
        </button>

        {/* Название */}
        <div>
          <label className="text-[10px] text-ink-dim uppercase tracking-wider font-semibold block mb-1.5">Название плана</label>
          <input
            value={planName}
            onChange={e => setPlanName(e.target.value)}
            placeholder="Например: Сплит грудь/спина"
            className="w-full bg-bg-card border border-line rounded-xl px-3 py-2.5 text-sm outline-none focus:border-emerald-glow/50 placeholder:text-ink-dim transition-colors"
          />
        </div>

        {/* Уровень */}
        <div>
          <label className="text-[10px] text-ink-dim uppercase tracking-wider font-semibold block mb-2">Твой уровень</label>
          <div className="grid grid-cols-3 gap-2">
            {LEVEL_OPTIONS.map(opt => (
              <button
                key={opt.value}
                onClick={() => { haptic('light'); setLevel(opt.value); }}
                className={`p-2.5 rounded-xl border text-left transition-all ${
                  level === opt.value
                    ? 'border-emerald-glow/60 bg-emerald-glow/10'
                    : 'border-line bg-bg-card'
                }`}
              >
                <div className={`text-[12px] font-bold ${level === opt.value ? 'text-emerald-glow' : ''}`}>{opt.label}</div>
                <div className="text-[9px] text-ink-dim mt-0.5">{opt.desc}</div>
                <div className="text-[9px] text-ink-muted mt-1">{opt.reps}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Цикл — пояснение */}
        <div className="bg-bg-card border border-line rounded-xl p-3">
          <div className="text-[11px] font-semibold mb-1">📅 Периодизация (4-недельный цикл)</div>
          <div className="grid grid-cols-4 gap-1 mt-2">
            {[
              { w: 'Нед 1', label: 'Адаптация', emoji: '🌱', color: 'text-sky-400' },
              { w: 'Нед 2', label: 'Прогрессия', emoji: '📈', color: 'text-emerald-400' },
              { w: 'Нед 3', label: 'Пик', emoji: '🔥', color: 'text-amber-400' },
              { w: 'Нед 4', label: 'Дилоад', emoji: '🔄', color: 'text-ink-dim' },
            ].map(p => (
              <div key={p.w} className="bg-bg-elevated rounded-lg p-2 text-center">
                <div className="text-base">{p.emoji}</div>
                <div className={`text-[9px] font-bold ${p.color}`}>{p.label}</div>
                <div className="text-[8px] text-ink-dim">{p.w}</div>
              </div>
            ))}
          </div>
          <div className="text-[10px] text-ink-dim mt-2">Цикл стартует с недели 1. Система автоматически переходит к следующей фазе после завершения всех тренировок недели.</div>
        </div>

        {/* Тренировочные дни */}
        <div className="space-y-4">
          {sessions.map((session) => {
            const hasConflict = session.scheduledDay !== null && dayConflicts.has(session.scheduledDay);
            return (
              <motion.div
                key={session.localId}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className={`bg-bg-card border rounded-2xl overflow-hidden transition-colors ${hasConflict ? 'border-amber-500/40' : 'border-line'}`}
              >
                {/* Заголовок */}
                <div className="flex items-center gap-2 p-3 border-b border-line">
                  <input
                    value={session.name}
                    onChange={e => updateSessionName(session.localId, e.target.value)}
                    className="flex-1 bg-transparent font-semibold text-sm outline-none"
                  />
                  {sessions.length > 1 && (
                    <button onClick={() => removeSession(session.localId)} className="p-1 text-ink-dim active:text-rose-400 transition-colors">
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>

                {/* День недели */}
                <div className="px-3 pt-2.5 pb-1">
                  <div className="text-[9px] text-ink-dim uppercase tracking-wider mb-1.5">День недели</div>
                  <div className="flex gap-1.5">
                    {DAYS_SHORT.map((d, idx) => {
                      const dayNum = idx + 1;
                      const isSelected = session.scheduledDay === dayNum;
                      const takenByOther = sessions.some(s => s.localId !== session.localId && s.scheduledDay === dayNum);
                      return (
                        <button
                          key={d}
                          onClick={() => !takenByOther && setSessionDay(session.localId, dayNum)}
                          disabled={takenByOther}
                          className={`flex-1 py-1.5 rounded-lg text-[11px] font-bold transition-all ${
                            isSelected
                              ? 'bg-emerald-glow text-bg'
                              : takenByOther
                              ? 'bg-bg-elevated text-ink-dim/30 cursor-not-allowed'
                              : 'bg-bg-elevated text-ink-dim active:bg-emerald-glow/20'
                          }`}
                        >
                          {d}
                        </button>
                      );
                    })}
                  </div>
                  {hasConflict && (
                    <div className="flex items-center gap-1 mt-1.5 text-[10px] text-amber-400">
                      <AlertCircle size={11} />
                      Мало времени для восстановления — рекомендуется 1 день отдыха между тренировками
                    </div>
                  )}
                </div>

                {/* Упражнения */}
                <div className="p-3 space-y-3">
                  {session.exercises.length === 0 && (
                    <div className="text-center py-3 text-[11px] text-ink-dim">Добавь упражнения</div>
                  )}

                  {session.exercises.map((ex, ei) => (
                    <div key={ei} className="bg-bg-elevated rounded-xl p-2.5">
                      <div className="flex items-center gap-2 mb-2">
                        {ex.gif_url && (
                          <img src={ex.gif_url} alt="" className="w-8 h-8 rounded-lg object-cover shrink-0 bg-bg-card" loading="lazy" />
                        )}
                        {ex.exercise_id ? (
                          <div className="flex-1 text-[12px] font-medium truncate">{ex.name}</div>
                        ) : (
                          <button
                            onClick={() => openPicker(session.localId, ei)}
                            className="flex-1 flex items-center gap-1 min-w-0 text-[12px] font-medium text-amber-400 text-left"
                          >
                            <AlertCircle size={12} className="shrink-0" />
                            <span className="truncate">{ex.name}</span>
                            <span className="text-[9px] text-ink-dim shrink-0">— выбрать</span>
                          </button>
                        )}
                        <button onClick={() => removeExercise(session.localId, ei)} className="p-1 text-ink-dim active:text-rose-400 transition-colors shrink-0">
                          <X size={13} />
                        </button>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        {[
                          { field: 'sets' as const, label: 'Подходы' },
                          { field: 'reps' as const, label: 'Повторения' },
                          { field: 'rest_seconds' as const, label: 'Отдых (с)' },
                        ].map(({ field, label }) => (
                          <div key={field} className="flex flex-col gap-0.5">
                            <span className="text-[9px] text-ink-dim uppercase tracking-wider">{label}</span>
                            <input
                              value={ex[field]}
                              onChange={e => updateExerciseField(session.localId, ei, field, e.target.value)}
                              className="bg-bg-card border border-line rounded-lg px-2 py-1.5 text-[12px] text-center outline-none focus:border-emerald-glow/50 w-full"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}

                  <button
                    onClick={() => openPicker(session.localId)}
                    className="w-full flex items-center justify-center gap-1.5 py-2 rounded-xl border border-dashed border-emerald-glow/30 text-emerald-glow text-[12px] active:bg-emerald-glow/5 transition-colors"
                  >
                    <Plus size={13} />
                    Добавить упражнение
                  </button>
                </div>
              </motion.div>
            );
          })}

          {sessions.length < 4 && (
            <button
              onClick={addSession}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl border border-dashed border-line text-ink-muted text-[13px] active:bg-bg-elevated transition-colors"
            >
              <Plus size={15} />
              Добавить тренировочный день
            </button>
          )}
        </div>

        {/* Автопрогрессия — opt-in предложение */}
        <button
          onClick={() => { haptic('light'); setAutoProgression(v => !v); }}
          className={`w-full flex items-center gap-3 rounded-2xl px-4 py-3 border transition-all ${autoProgression ? 'bg-emerald-glow/10 border-emerald-glow/40' : 'bg-bg-card border-line'}`}
        >
          <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${autoProgression ? 'bg-emerald-glow/20' : 'bg-bg-elevated'}`}>
            <Sparkles size={18} className={autoProgression ? 'text-emerald-glow' : 'text-ink-dim'} />
          </div>
          <div className="flex-1 text-left">
            <div className="text-[13px] font-semibold">Автопрогрессия нагрузки</div>
            <div className="text-[10px] text-ink-dim">Будем предлагать повышать вес по неделям · скоро</div>
          </div>
          <div className={`w-10 h-6 rounded-full p-0.5 transition-colors shrink-0 ${autoProgression ? 'bg-emerald-glow' : 'bg-line'}`}>
            <div className={`w-5 h-5 rounded-full bg-white transition-transform ${autoProgression ? 'translate-x-4' : ''}`} />
          </div>
        </button>
      </div>

      <div className="fixed bottom-0 inset-x-0 p-4 bg-bg border-t border-line">
        <button
          onClick={savePlan}
          disabled={saving}
          className="w-full flex items-center justify-center gap-2 bg-emerald-glow text-bg font-bold py-3.5 rounded-2xl active:scale-[0.98] transition-all disabled:opacity-50"
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
          {saving ? 'Сохраняем...' : 'Сохранить план'}
        </button>
      </div>

      {/* Поиск упражнений */}
      <AnimatePresence>
        {pickerSession && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex flex-col bg-bg"
          >
            <div className="flex items-center gap-3 px-4 pt-4 pb-3 border-b border-line">
              <button onClick={closePicker} className="p-2 -ml-2"><X size={20} /></button>
              <div className="flex-1 flex items-center gap-2 bg-bg-elevated border border-line rounded-xl px-3 py-2">
                <Search size={14} className="text-ink-dim shrink-0" />
                <input
                  autoFocus
                  value={searchQuery}
                  onChange={e => handleSearchChange(e.target.value)}
                  placeholder="Найти упражнение..."
                  className="flex-1 bg-transparent text-sm outline-none placeholder:text-ink-dim"
                />
                {searchLoading && <Loader2 size={13} className="animate-spin text-ink-dim shrink-0" />}
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
              {!searchQuery && <div className="text-center py-12 text-ink-dim text-sm">Введи название упражнения</div>}
              {searchQuery && !searchLoading && searchResults.length === 0 && (
                <div className="text-center py-12 text-ink-dim text-sm">Ничего не найдено</div>
              )}
              {searchResults.map(ex => (
                <button
                  key={ex.id}
                  onClick={() => pickExercise(ex)}
                  className="w-full flex items-center gap-3 bg-bg-card border border-line rounded-xl p-3 active:bg-bg-elevated transition-colors"
                >
                  {ex.gif_url ? (
                    <img src={ex.gif_url} alt="" className="w-10 h-10 rounded-lg object-cover shrink-0 bg-bg-elevated" loading="lazy" />
                  ) : (
                    <div className="w-10 h-10 rounded-lg bg-bg-elevated flex items-center justify-center shrink-0 text-lg">💪</div>
                  )}
                  <div className="flex-1 text-left min-w-0">
                    <div className="text-sm font-medium truncate">{ex.name}</div>
                    <div className="text-[11px] text-ink-dim truncate">{ex.target ?? ex.category}</div>
                  </div>
                  <Plus size={16} className="text-emerald-glow shrink-0" />
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Импорт плана текстом */}
      <AnimatePresence>
        {showImport && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex flex-col bg-bg"
          >
            <div className="flex items-center gap-3 px-4 pt-4 pb-3 border-b border-line">
              <button onClick={() => setShowImport(false)} className="p-2 -ml-2"><X size={20} /></button>
              <div className="flex-1">
                <div className="font-semibold text-sm">Вставить план текстом</div>
                <div className="text-[10px] text-ink-dim">Свой план или от тренера — распознаем сами</div>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
              <div className="text-[11px] text-ink-dim leading-relaxed bg-bg-card border border-line rounded-xl p-3">
                Пиши как удобно. Пример:<br />
                <span className="text-ink-muted">Пн (грудь): жим лёжа 4х8, разводка 3х12<br />Ср (спина): становая 5х5, тяга 4х8</span>
              </div>
              <textarea
                value={importText}
                onChange={e => setImportText(e.target.value)}
                placeholder="Вставь или впиши свой план…"
                rows={12}
                className="w-full bg-bg-card border border-line rounded-xl px-3 py-3 text-sm outline-none focus:border-emerald-glow/50 placeholder:text-ink-dim resize-none"
              />
            </div>
            <div className="p-4 border-t border-line">
              <button
                onClick={runImport}
                disabled={importing}
                className="w-full flex items-center justify-center gap-2 bg-emerald-glow text-bg font-bold py-3.5 rounded-2xl active:scale-[0.98] transition-all disabled:opacity-50"
              >
                {importing ? <Loader2 size={16} className="animate-spin" /> : <ClipboardList size={16} />}
                {importing ? 'Распознаём…' : 'Распознать план'}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
